import { env } from "../../../config/env";

const BASE_PATH = "/api/v2";

function normalizeHost(host: string): string {
  return host.replace(/\/+$/, "");
}

function getInstallContextUrl(path: string): string {
  return `${normalizeHost(env.apiUrl)}${BASE_PATH}${path}`;
}

type InstallContextRequestOptions = {
  allowNotFound?: boolean;
};
type InstallContextResponseParser<T> = (raw: unknown) => T;

type InstallContextAttemptError = {
  method: "GET";
  url: string;
  contentType?: string;
  message: string;
  status?: number;
};

function sanitizeLogMessage(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 240);
}

function summarizeAttemptErrors(
  attemptErrors: InstallContextAttemptError[]
): string {
  return attemptErrors
    .map((attempt, index) => {
      const status = attempt.status ?? "network_error";
      const contentTypePart =
        attempt.contentType === undefined ? "" : ` type=${attempt.contentType}`;
      return `#${index + 1} method=${attempt.method} status=${status}${contentTypePart} url=${attempt.url} message="${sanitizeLogMessage(attempt.message)}"`;
    })
    .join(" | ");
}

function withQuery(url: string, payload: unknown): string {
  if (
    payload === null ||
    typeof payload !== "object" ||
    Array.isArray(payload)
  ) {
    return url;
  }

  const parsed = new URL(url);
  for (const [key, value] of Object.entries(
    payload as Record<string, unknown>
  )) {
    if (value === null || value === undefined) continue;
    parsed.searchParams.set(key, String(value));
  }

  return parsed.toString();
}

export async function requestInstallContextJson<T>(
  path: string,
  payload: unknown,
  parse: InstallContextResponseParser<T>,
  options?: InstallContextRequestOptions
): Promise<T | null> {
  const queryUrl = withQuery(getInstallContextUrl(path), payload);
  // `include` carries the ddl_fp cookie between store and match. Some Android
  // in-app WebViews send the request as `Origin: null` even from a real page
  // origin, and the api answers `null` without Allow-Credentials (reflecting
  // it with credentials was a High CORS finding), so a credentialed fetch
  // fails the CORS check: no response, a TypeError. The client cannot know
  // beforehand which Origin the WebView serializes, so the fallback is on that
  // failure: retry once without credentials. Nothing is lost, the cookie is
  // SameSite=Lax and never left such a context anyway.
  const credentialModes: RequestCredentials[] = globalThis.isSecureContext
    ? ["include", "omit"]
    : ["omit"];
  const attemptErrors: InstallContextAttemptError[] = [];

  for (const credentials of credentialModes) {
    let response: Response;
    try {
      response = await fetch(queryUrl, {
        method: "GET",
        mode: "cors",
        credentials,
      });
    } catch (error) {
      attemptErrors.push({
        method: "GET",
        url: queryUrl,
        message: `credentials=${credentials}: ${error instanceof Error ? error.message : String(error)}`,
      });
      continue;
    }

    // An HTTP response, whatever its status, ends the credentials fallback.
    try {
      const responseText = await response.text();
      const responseContentType =
        response.headers.get("content-type")?.toLowerCase() ?? "";

      if (!response.ok) {
        if (response.status === 404 && options?.allowNotFound) {
          const notFoundMessage = sanitizeLogMessage(responseText);
          console.info(
            `DDL: install-context no-match path=${path} status=404 message="${notFoundMessage || "Fingerprint not found or expired"}"`
          );
          return null;
        }
        attemptErrors.push({
          method: "GET",
          url: queryUrl,
          status: response.status,
          message: sanitizeLogMessage(responseText),
        });
      } else if (responseContentType.includes("application/json")) {
        let parsedBody: unknown;
        try {
          parsedBody = JSON.parse(responseText) as unknown;
        } catch (error) {
          attemptErrors.push({
            method: "GET",
            url: queryUrl,
            contentType: responseContentType,
            message: `invalid json response: ${error instanceof Error ? error.message : String(error)}`,
          });
        }
        if (parsedBody !== undefined) {
          try {
            return parse(parsedBody);
          } catch (error) {
            attemptErrors.push({
              method: "GET",
              url: queryUrl,
              contentType: responseContentType,
              message: `invalid response payload: ${error instanceof Error ? error.message : String(error)}`,
            });
          }
        }
      } else {
        attemptErrors.push({
          method: "GET",
          url: queryUrl,
          message: `unexpected content-type: ${responseContentType || "empty"}`,
        });
      }
    } catch (error) {
      attemptErrors.push({
        method: "GET",
        url: queryUrl,
        message: error instanceof Error ? error.message : String(error),
      });
    }
    break;
  }

  const attemptsSummary = summarizeAttemptErrors(attemptErrors);
  console.error(
    `DDL: install-context request failed path=${path} attempts=${attemptsSummary}`
  );
  throw new Error(`install-context request failed: ${path}`);
}
